import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso } from './iso.js';
import type { BoxPiece, FurniDrawer } from './iso.js';

/** Drawers for the festive furniture set: CNY, Hari Raya, Deepavali and Christmas. */

/* -------------------------------------------------------------------------- */
/* Items                                                                      */
/* -------------------------------------------------------------------------- */

function drawCnyLanternArch(pc: PixelCanvas, rotation: number): void {
  const post = faces('#9b1d1d', '#c23030');
  const beam = faces('#c9a227', '#e8c250');
  const alongV = rotation === 2 || rotation === 6;
  const posts: BoxPiece[] = alongV
    ? [box(-0.46, -0.34, -0.08, 0.08, 0, 70, post), box(0.34, 0.46, -0.08, 0.08, 0, 70, post)]
    : [box(-0.08, 0.08, -0.46, -0.34, 0, 70, post), box(-0.08, 0.08, 0.34, 0.46, 0, 70, post)];
  const beamPiece = alongV
    ? box(-0.46, 0.46, -0.1, 0.1, 70, 80, beam)
    : box(-0.1, 0.1, -0.46, 0.46, 70, 80, beam);
  drawBoxes(pc, [...posts, beamPiece]);

  // Lanterns hung along the beam.
  for (const o of [-0.3, 0, 0.3]) {
    const [cu, cv] = alongV ? [o, 0] : [0, o];
    drawCylinder(pc, 0.07, 54, 68, '#e8c250', '#c23030', cu, cv);
    const tip = iso(cu, cv, 54);
    pc.rect(tip.x - 1, tip.y, 2, 4, '#e8c250');
  }
}

function drawPineappleTartStand(pc: PixelCanvas, rotation: number): void {
  const body = faces('#e8c250', '#f3d980');
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 0, 26, body));
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 26, 30, faces('#9b1d1d', '#c23030')));

  const face = frontFace(rotation) ?? 'left';
  const at = 0.44;
  faceQuad(pc, face, at, -0.38, 0.38, 4, 22, '#c9a227');
  for (let i = 0; i < 3; i += 1) {
    const s0 = -0.3 + i * 0.22;
    faceQuad(pc, face, at, s0, s0 + 0.16, 6 + i * 2, 16 + i * 2, '#f0bf36');
  }
  const top = iso(0, 0, 30);
  for (const [dx, dy] of [[-10, -2], [0, -4], [10, -2]] as const) {
    pc.ellipse(top.x + dx, top.y + dy, 4, 3, '#f0bf36');
    pc.px(top.x + dx, top.y + dy - 1, '#c9a227');
  }
}

function drawHariRayaLights(pc: PixelCanvas, rotation: number): void {
  const alongV = rotation === 2 || rotation === 6;
  const a = alongV ? iso(-0.46, 0, 60) : iso(0, -0.46, 60);
  const b = alongV ? iso(0.46, 0, 60) : iso(0, 0.46, 60);
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 + 10 };
  pc.line(a.x, a.y, mid.x, mid.y, '#2f7f3c');
  pc.line(mid.x, mid.y, b.x, b.y, '#2f7f3c');

  const colors = ['#f0bf36', '#ef7d2c', '#3d9e4c', '#f0bf36'];
  for (let i = 0; i <= 8; i += 1) {
    const t = i / 8;
    const x = a.x + (b.x - a.x) * t;
    const y = t < 0.5 ? a.y + (mid.y - a.y) * (t / 0.5) : mid.y + (b.y - mid.y) * ((t - 0.5) / 0.5);
    pc.px(x, y + 2, colors[i % colors.length]!);
  }
}

function drawKetupatDecor(pc: PixelCanvas, rotation: number): void {
  const face = frontFace(rotation) ?? 'left';
  const hook = iso(0, 0, 50);
  pc.line(hook.x, hook.y - 6, hook.x, hook.y, '#5a3a20');

  const colors = ['#3d9e4c', '#5cbd63'];
  const offsets = face === 'left' ? [-6, 6] : [6, -6];
  offsets.forEach((dx, i) => {
    const cy = hook.y + 6 + i * 2;
    pc.poly(
      [
        { x: hook.x + dx, y: cy - 6 },
        { x: hook.x + dx + 5, y: cy },
        { x: hook.x + dx, y: cy + 6 },
        { x: hook.x + dx - 5, y: cy },
      ],
      colors[i % colors.length]!,
    );
  });
}

function drawDeepavaliDiyas(pc: PixelCanvas, rotation: number): void {
  const petal = (r: number, color: string) =>
    pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
  petal(0.42, '#9b1d1d');
  petal(0.32, '#e8c250');
  petal(0.2, '#c23030');

  const positions: Array<[number, number]> = [
    [0.28, 0],
    [-0.28, 0],
    [0, 0.28],
    [0, -0.28],
  ];
  positions.forEach(([u, v], i) => {
    drawCylinder(pc, 0.06, 0, 6, '#c9a227', '#9b6a1f', u, v);
    const flame = iso(u, v, 9);
    const flicker = (rotation / 2 + i) % 2 === 0 ? '#ffb347' : '#ff8a3d';
    pc.ellipse(flame.x, flame.y, 2, 3, flicker);
  });
}

function drawChristmasTree(pc: PixelCanvas, rotation: number): void {
  drawCylinder(pc, 0.22, 0, 6, '#6b4423', '#4a2f17');
  const dark = '#1f6b34';
  const mid = '#2f8f45';
  const light = '#4aab5c';
  // Apex up, wide base down — each tier narrower and higher than the last.
  pc.poly([{ x: 0, y: -50 }, { x: -26, y: -18 }, { x: 26, y: -18 }], dark);
  pc.poly([{ x: 0, y: -66 }, { x: -20, y: -38 }, { x: 20, y: -38 }], mid);
  pc.poly([{ x: 0, y: -80 }, { x: -13, y: -58 }, { x: 13, y: -58 }], light);
  pc.poly([{ x: 0, y: -86 }, { x: -3, y: -80 }, { x: 3, y: -80 }], '#f0bf36');

  // Ornaments shift slightly with rotation for a bit of sparkle variety.
  const ornamentColors = ['#d9433b', '#f0bf36', '#5ad1ff'];
  const offset = rotation / 2;
  for (let i = 0; i < 6; i += 1) {
    const idx = (i + offset) % ornamentColors.length;
    const x = -18 + i * 7;
    const y = -40 - (i % 3) * 14;
    pc.px(x, y, ornamentColors[idx]!);
  }
}

function drawNianGaoTable(pc: PixelCanvas, rotation: number): void {
  drawCylinder(pc, 0.05, 0, 24, '#5a3a20', '#3e2715');
  drawCylinder(pc, 0.4, 24, 28, '#9b1d1d', '#c23030');

  const face = frontFace(rotation) ?? 'left';
  const at = 0.4;
  faceQuad(pc, face, at, -0.3, 0.3, 24, 27, '#e8c250');

  const top = iso(0, 0, 28);
  pc.ellipse(top.x, top.y - 2, 10, 5, '#8a4a1f');
  pc.ellipse(top.x, top.y - 3, 7, 3, '#b2652b');
  pc.rect(top.x - 1, top.y - 10, 2, 7, '#3d9e4c');
}

function drawFirecrackerString(pc: PixelCanvas, rotation: number): void {
  const face = frontFace(rotation) ?? 'left';
  const top = iso(0, 0, 70);
  pc.line(top.x, top.y, top.x, top.y - 8, '#5a3a20');

  const dir = face === 'left' ? -1 : 1;
  for (let i = 0; i < 5; i += 1) {
    const y = top.y + i * 10;
    const x = top.x + dir * i * 1.5;
    pc.rect(x - 2, y, 4, 8, '#c23030');
    pc.rect(x - 2, y, 4, 2, '#e8c250');
  }
}

export const FESTIVE_DRAWERS: Record<string, FurniDrawer> = {
  cny_lantern_arch: drawCnyLanternArch,
  pineapple_tart_stand: drawPineappleTartStand,
  hari_raya_lights: drawHariRayaLights,
  ketupat_decor: drawKetupatDecor,
  deepavali_diyas: drawDeepavaliDiyas,
  christmas_tree: drawChristmasTree,
  nian_gao_table: drawNianGaoTable,
  firecracker_string: drawFirecrackerString,
};
